import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { forceAppUpdate } from "./lib/forceAppUpdate";
import { isTypingInInput, showUpdateBanner } from "./hooks/useUpdateBanner";

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Every route is its own lazily-loaded, content-hashed JS chunk (see
// App.tsx's route-level code splitting). When a new version deploys,
// those hashes change and the old chunk files are gone -- so anyone who
// already has the site open in a tab, then navigates to a route whose
// chunk changed since their tab loaded, gets a real fetch failure for a
// file that no longer exists. Vite fires this specific event for
// exactly that situation (a failed dynamic import/chunk preload), so we
// can recover automatically instead of showing an error screen: reload
// once to pick up the current index.html, which points at the correct,
// current chunk files.
//
// The reloaded-flag guards against a loop -- if the reload doesn't
// actually fix it (a real, persistent network issue, or a genuine bug
// that happens to produce the same error), we only try the automatic
// fix once per tab session, then let it surface normally through
// ErrorBoundary's fallback UI with its manual reload button instead of
// reloading forever.
//
// The isTypingInInput() check fixes a real, confirmed bug: this
// handler previously force-reloaded unconditionally, which is a hard
// navigation (window.location.replace()) that wipes any in-progress,
// uncommitted state -- including whatever someone was mid-typing into
// a search box at the exact moment a stale chunk failure fired (the
// three search call sites in CommandPalette.tsx/Layout.tsx/Home.tsx
// now also catch their own import failures directly and won't even
// reach this global handler anymore, but this check stays as a second
// layer of protection for any other lazy import elsewhere in the app
// that might fail while someone's actively typing).
window.addEventListener("vite:preloadError", () => {
  if (sessionStorage.getItem("chunk-reload-attempted")) return;
  // On a tool page the visitor may be holding a finished result; never reload under them (PX-13).
  if (isTypingInInput() || window.location.pathname.startsWith("/tool/")) {
    showUpdateBanner();
    return;
  }
  sessionStorage.setItem("chunk-reload-attempted", "true");
  // Hard navigation beats a soft reload when index.html is HTTP-cached.
  forceAppUpdate();
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);

// Register the (deliberately non-caching) service worker immediately,
// not deferred to window's load event -- a registered service worker
// is one of Chrome's real requirements for offering PWA installation,
// and waiting for every image/style/script on the page to finish
// loading first (which is what the load event actually waits for) can
// push registration later than Chrome's own installability check runs,
// for no real benefit here since this service worker deliberately does
// no caching that would need to compete with initial page load.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch(err => {
    console.warn("Service worker registration failed:", err);
  });
}
